import { LoadingButton } from '@mui/lab'
import { Box, Typography, Card, CircularProgress } from '@mui/material'
import { AddressElement, Elements } from '@stripe/react-stripe-js'
import { Stripe, StripeElements } from '@stripe/stripe-js'
import React, { useEffect, useState, Dispatch, SetStateAction } from 'react'
import { useParams } from 'react-router-dom'

import apiClient from '../../../api/apiClient'
import { CheckoutForm } from '../../../components/payment-service/CheckoutForm'
import TaxForm from '../../../components/payment-service/TaxForm'
import { useSnackbarContext } from '../../../hooks/useSnackBar'
import { useStripePromise } from '../../../hooks/useStripePromise'
import { ITaxDetailsPayload, useTierSelectionContext } from '../../../hooks/useTierSelection'

export type StripeStateType = {
  stripe: Stripe
  elements: StripeElements
}

function BillingForm({
  clientSecret,
  setActiveStep,
  stripeKey,
}: {
  clientSecret: string
  setActiveStep: Dispatch<SetStateAction<number>>
  stripeKey: string
}) {
  const { customerProfile, addTaxDetails, updateTaxDetails, deleteTaxDetails } = useTierSelectionContext()
  const { openSnackBar } = useSnackbarContext()

  const [isLoading, setIsLoading] = useState(false)
  const [addressLoading, setAddressLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [stripeState, setStripeState] = useState<StripeStateType | null>(null)
  const [isCardComplete, setIsCardComplete] = useState<boolean>(false)
  const [isAddressComplete, setIsAddressComplete] = useState<boolean>(false)
  const [invalidTaxDetails, setInvalidTaxDetails] = useState<boolean>(false)
  const [isFormLoading, setIsFormLoading] = useState<boolean>(true)
  const [taxDetails, setTaxDetails] = useState<ITaxDetailsPayload>({
    type: customerProfile?.provider?.tax_ids?.[0]?.type ?? '',
    value: customerProfile?.provider?.tax_ids?.[0]?.value ?? '',
  })

  const { stripePromise } = useStripePromise(stripeKey)
  const { transferWorkspaceId } = useParams()

  const onPaymentSuccess = async () => {
    openSnackBar({
      message: 'Payment method added successfully!',
      type: 'success',
    })
  }

  const onSave = async () => {
    try {
      setAddressLoading(true)
      if (!stripeState) {
        console.log('Stripe has not loaded yet.')
        return
      }

      const addressElement = stripeState.elements.getElement('address')
      if (!addressElement) {
        console.error('Address element not found')
        setAddressLoading(false)
        return
      }
      const { complete, value } = await addressElement.getValue()

      const payload = {
        name: value.name,
        phone: value.phone === '' || !value.phone ? null : value.phone,
        address: value.address,
      }

      const taxId = customerProfile?.provider?.tax_ids?.[0]

      if (taxId && taxDetails.value === '') {
        await deleteTaxDetails(taxId.id)
      } else if (taxDetails.value !== '' && (!taxId || taxId?.value !== taxDetails.value)) {
        if (taxId) {
          await updateTaxDetails(taxId?.id, taxDetails)
        } else {
          await addTaxDetails(taxDetails)
        }
      }

      if (complete && payload) {
        await apiClient.patch(`/workspace/${transferWorkspaceId}/subscriptions/customer/profile`, payload)

        openSnackBar({
          message: 'Address updated successfully!',
          type: 'success',
        })
      }
      setAddressLoading(false)
    } catch (error) {
      console.error(error)
      openSnackBar({
        message: 'Something went wrong.',
        type: 'error',
      })
      setAddressLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!stripeState) {
      console.log('Stripe has not loaded yet.')
      return
    }

    const { stripe, elements } = stripeState
    await elements.submit()
    setIsLoading(true)

    const { error } = await stripe.confirmSetup({
      elements,
      clientSecret,
      redirect: 'if_required',
    })
    let errorMessage = ''
    if (!error) {
      if (!customerProfile?.provider.profile.address) {
        const addressElement = stripeState.elements.getElement('address')
        if (!addressElement) {
          console.error('Address element not found')
          setIsLoading(false)
          return
        }
        const { complete, value } = await addressElement.getValue()

        const taxId = customerProfile?.provider?.tax_ids?.[0]

        if (taxId && taxDetails.value === '') {
          await deleteTaxDetails(taxId.id)
        } else if (taxDetails.value !== '' && (!taxId || taxId?.value !== taxDetails.value)) {
          if (taxId) {
            await updateTaxDetails(taxId?.id, taxDetails)
          } else {
            await addTaxDetails(taxDetails)
          }
        }

        const payload = {
          name: value.name,
          phone: value.phone === '' || !value.phone ? null : value.phone,
          address: value.address,
        }

        if (complete && payload) {
          await apiClient.patch(`/workspace/${transferWorkspaceId}/subscriptions/customer/profile`, payload)
          openSnackBar({
            message: 'Address updated successfully!',
            type: 'success',
          })
        }
      }

      openSnackBar({
        message: 'Payment method added successfully!',
        type: 'success',
      })
      setIsLoading(false)
      setActiveStep((prev) => prev + 1)

      return
    }
    if (error?.type === 'card_error' || error?.type === 'validation_error') {
      errorMessage = error.message || ''
      setErrorMessage(errorMessage)
    } else {
      errorMessage = 'An unexpected error occurred.'
      setErrorMessage(errorMessage)
    }
    openSnackBar({
      message: errorMessage,
      type: 'error',
    })
    setIsLoading(false)
  }

  const appearance = {
    theme: 'stripe',
    labels: 'floating',
  }

  const options = {
    appearance,
    clientSecret,
  }

  const handleTaxDetailsChange = (type: string, value: string) => {
    setTaxDetails((prevTaxDetails) => ({
      ...prevTaxDetails,
      [type]: value,
    }))
  }

  return (
    <>
      {stripePromise ? (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <Elements options={options} stripe={stripePromise}>
          <Card
            sx={{
              backgroundColor: '#fff',
              padding: 3,
              margin: '12px 0px',
              borderRadius: '12px',
            }}
          >
            <Box>
              <Typography variant="h4" sx={{ padding: '12px 0px' }}>
                Add billing details
              </Typography>
            </Box>

            <TaxForm
              taxDetails={taxDetails}
              handleTaxDetailsChange={handleTaxDetailsChange}
              setInvalidTaxDetails={setInvalidTaxDetails}
              isLoading={isFormLoading}
            />

            <AddressElement
              options={{
                mode: 'billing',
                fields: {
                  phone: 'always',
                },
                validation: {
                  phone: {
                    required: 'never',
                  },
                },
                defaultValues: {
                  name: customerProfile?.provider.profile.name,
                  phone: customerProfile?.provider.profile.phone,
                  address: customerProfile?.provider.profile.address || undefined,
                },
              }}
              onReady={() => setIsFormLoading(false)}
            />
            <Box sx={{ padding: '12px 0px', display: 'flex', justifyContent: 'flex-end' }}>
              <LoadingButton disabled={invalidTaxDetails} onClick={onSave} loading={addressLoading} variant="contained">
                Save
              </LoadingButton>
            </Box>
          </Card>
          <Card
            sx={{
              backgroundColor: '#fff',
              padding: 3,
              margin: '12px 0px',
              borderRadius: '12px',
            }}
          >
            <Box>
              <Typography variant="h4" sx={{ padding: '12px 0px' }}>
                Add payment methods
              </Typography>
            </Box>
            <CheckoutForm
              onPaymentSuccess={onPaymentSuccess}
              clientSecret={clientSecret}
              onStripeLoaded={setStripeState}
              setIsAddressComplete={setIsAddressComplete}
              setIsCardComplete={setIsCardComplete}
            />
            <Box sx={{ padding: '12px 0px', display: 'flex', justifyContent: 'flex-end' }}>
              <LoadingButton
                disabled={!isCardComplete || !isAddressComplete || invalidTaxDetails}
                onClick={handleSubmit}
                loading={isLoading}
                variant="contained"
              >
                Save and confirm
              </LoadingButton>
            </Box>
          </Card>
        </Elements>
      ) : (
        <CircularProgress />
      )}
    </>
  )
}

function BillingDetails({ setActiveStep }: { setActiveStep: Dispatch<SetStateAction<number>> }) {
  const [stripeKey, setStripeKey] = useState<string>('')
  const [clientSecret, setClientSecret] = useState<string>('')

  const { transferWorkspaceId } = useParams()

  useEffect(() => {
    if (transferWorkspaceId && transferWorkspaceId !== '') {
      const onAddNewPaymentMethod = async () => {
        try {
          const response = await apiClient.post<any, any>(
            `/workspace/${transferWorkspaceId}/subscriptions/customer/payment_method`,
          )
          setClientSecret(response.data.data.client_secret)
          setStripeKey(response.data.data.stripe_key)
        } catch (error) {
          console.error(error)
        }
      }
      onAddNewPaymentMethod()
    }
  }, [transferWorkspaceId])

  return (
    <Box>
      {clientSecret && stripeKey && clientSecret !== '' && stripeKey !== '' && (
        <BillingForm clientSecret={clientSecret} setActiveStep={setActiveStep} stripeKey={stripeKey as string} />
      )}
    </Box>
  )
}

export default BillingDetails
